import {
  any,
  array,
  boolean,
  enumType,
  nullable,
  number,
  object,
  optional,
  Output,
  record,
  string,
  uuid,
} from "valibot";

import { LocaleSchema } from "shared/schemas";

const WidgetPosition = enumType(["left", "right"] as const);

export const StarterQuestionSchema = object({
  id: string([uuid()]),
  question: string(),
});

export const StarterQuestionsSchema = array(StarterQuestionSchema);

export type AfterMessageQuestion =
  | ({
      type: "starter-question";
    } & Output<typeof StarterQuestionSchema>)
  | {
      type: "follow-up-question";
      question: string;
      is_support_request?: boolean;
      integration_config_type?: Output<typeof IntegrationTypeSchema>;
    };
const defaultImage =
  "https://writesonic-frontend.s3.us-east-1.amazonaws.com/frontend-assets/templates-new/BotsonicNew.png";

export const BotInfoSchema = object({
  id: string([uuid()]),
  bot_info_config: object({
    show_post_chat_feedback: boolean(),
    chatbot_color: string(),
    company_name: string(),
    company_logo: nullable(string(), defaultImage),
    chatbot_avatar: nullable(string(), defaultImage),
    welcome_message: nullable(string(), "Hey there, how can I help you?"),
    chatbot_subheading: string(),
    support_contact_number: nullable(string()),
    is_show_chat_sonic_brand: boolean(),
    button_icon: string(),
    is_show_sources: boolean(),
    widget_position: WidgetPosition,
    is_open_on_initial_render: boolean(),
    is_show_recaptcha: boolean(),
    custom_placeholder: string(),
    show_starter_widget: boolean(),
    hide_user_form: boolean(),
    server_error_message: string(),
    no_sources_message: optional(
      string(),
      "The bot is yet to be trained, please add the data and train the bot.",
    ),
    show_floating_welcome_message: boolean(),
  }),
  bot_prompt_config: object({
    generation_language: LocaleSchema,
    is_conversational: boolean(),
  }),
});

export const ConfigurationFieldTypeSchema = enumType([
  "string",
  "email",
  "number",
  "file_url",
  "date",
  "phone_number",
  "text_area",
]);

export const UserDataElementSchema = object({
  id: string(),
  key: string(),
  type: ConfigurationFieldTypeSchema,
  placeholder: optional(string()),
  is_required: boolean(),
  is_deleted: boolean(),
});

export const ChatUserFormFieldsSchema = object({
  data_fields: array(UserDataElementSchema),
});

export const SSEErrorSchema = object({
  status_code: number(),
  error_message: string(),
});

export const IntegrationTypeSchema = enumType([
  "calendly",
  "calendar",
  "whatsapp",
  "messenger",
  "slack",
  "telegram",
  "zendesk",
  "freshdesk",
  "zendesk_live_agent_handoff",
  "salesforce_leads",
  "salesforce_live_agent_handoff",
  "follow_up_ques_support_request",
]);

export const ChatMessageTypeSchema = enumType([
  "text",
  "live_agent_info",
  "waiting_for_live_agent",
  "handoff_canceled",
]);

export const SourceSchema = object({
  url: string(),
  content: string(),
});

export const IntegrationOptionsSchema = object({
  name: string(),
  type: string(),
  id: string(),
  title: optional(string()),
  icon: optional(string()),
});

export const IntegrationFormFieldsSchema = object({
  name: string(),
  type: string(),
  label: string(),
  placeholder: string(),
  required: boolean(),
  value: optional(string()),
});

export const IntegrationInfoSchema = object({
  id: string(),
  config_type: string(),
  required_user_fields: array(IntegrationFormFieldsSchema),
});

const UserIntegrationDataSchema = object({
  submitted_user_fields: array(IntegrationFormFieldsSchema),
});

export const IntegrationSchema = object({
  type: IntegrationTypeSchema,
  link: optional(string()),
  data: optional(object({})),
  available: optional(array(IntegrationOptionsSchema)),
  integration_info: optional(IntegrationInfoSchema),
  user_integration_data: optional(UserIntegrationDataSchema),
});

export const IntegrationCredentialsSchema = record(string(), string());

export const BaseIntegrationSchema = object({
  name: string(),
  description: string(),
  icon: string(),
  credentials: IntegrationCredentialsSchema,
  integration_type: enumType(["button", "text"]),
  is_active: boolean(),
  button_name: string(),
});

export const BaseIntegrationsSchema = array(BaseIntegrationSchema);

export const LocationBasedConsentInfo = object({
  is_required: boolean(),
  message: nullable(string()),
  updated_at: nullable(string()),
});

export const UUIDSchema = string([uuid()]);

export const SerializedChatDataSchema = object({
  // chatData: ChatDataSchema,
  chatId: UUIDSchema,
});

export const InstalledApps = array(
  object({
    config_type: string(),
    is_installed: boolean(),
    is_active: boolean(),
    is_enabled: boolean(),
  }),
);

export const UrlUpdateMapSchema = record(string(), string());

export const AgentInfo = object({
  id: optional(string()),
  name: optional(string()),
  email: optional(string()),
  agent_avatar: optional(string()),
});

export const LiveAgentHandoffInfo = object({
  ticket_id: optional(string()),
  bot_integration_id: optional(string()),
  integration_config_type: optional(string()),
  ticket_status: optional(string()),
  agent_info: optional(AgentInfo),
  author_id: optional(string()),
  is_agent_connected: optional(boolean()),
  is_active: optional(boolean()),
  created_at: optional(string()),
  updated_at: optional(string()),
});

export const AttachmentSchema = object({
  url: string(),
  file_name: string(),
  mimetype: string(),
  file_size: number(),
  file_key: string(),
});

export const SupportTicketInfoSchema = object({
  ticket_id: optional(string()),
  ticket_url: optional(string()),
  title: string(),
  name: string(),
  email: string(),
  description: optional(string()),
  status: optional(string()),
  attachments: optional(array(AttachmentSchema)),
  created_at: string(),
  updated_at: string(),
});

export const ChatDataAdditionalInfoSchema = object({
  agentInfo: optional(AgentInfo),
  chatMessageType: optional(ChatMessageTypeSchema),
  createdTicketInfo: optional(SupportTicketInfoSchema),
});

export const FollowUpQuestionSchema = object({
  question: string(),
  integration_config_type: optional(IntegrationTypeSchema),
  is_support_request: optional(boolean()),
});

export const UserFormInfoSchema = object({
  form_field: object({
    id: string(),
    question: string(),
    type: enumType(["text", "textarea", "email", "list_option", "attachments"]),
    value: optional(any()),
    required: boolean(),
    placeholder: optional(string()),
    options: optional(
      array(
        object({
          label: string(),
          value: string(),
        }),
      ),
    ),
    min_length: optional(number()),
    max_length: optional(number()),
    max_file_size: optional(number()),
  }),
  form_id: string(),
  submitted_form_id: string(),
  is_skippable: boolean(),
  skip_submission: optional(boolean()),
});

export const ChatDataSchema = array(
  object({
    id: optional(string()),
    message: string(),
    sent: boolean(),
    sources: optional(array(SourceSchema)),
    follow_ups: optional(array(FollowUpQuestionSchema)),
    integration: optional(IntegrationSchema),
    showLikeDislikeBtn: optional(boolean()),
    imageUrls: optional(array(string())),
    additionalInfo: optional(ChatDataAdditionalInfoSchema),
    likeStatus: optional(string()),
    user_form_info: optional(UserFormInfoSchema),
    submitted_form_field_data: optional(UserFormInfoSchema),
    is_intermediate: optional(boolean()),
    renderableSource: optional(string()),
  }),
);

export const ChatHistory = object({
  chat_data: ChatDataSchema,
  live_agent_handoff_info: nullable(LiveAgentHandoffInfo),
  chat_ended: optional(boolean()),
});
